import React, { useMemo } from 'react';
import {
    countAppointmentsByDate,
    formatShortDate,
    isDocEmpty,
    tiptapDocToPlainText
} from '@odo/shared-ui';
import PatientPicker from './PatientPicker';
import ChartNotesField from './ChartNotesField';
import DateField from './DateField';
import TimeField from './TimeField';
import OdoContextCard from './OdoContextCard';
import type { Appointment } from '../../services/appointments';
import type { DashboardPatient } from '../../services/patient';
import type { AppointmentDraft } from './types';
import {
    APPOINTMENT_REASONS,
    MAX_APPOINTMENT_REASON_LENGTH
} from '../../utils/appointments/reasons';
import { MAX_NOTE_BODY_LENGTH } from '../patient/notes/noteAccess';
import {
    buildAppointmentNoteTitle,
    checkChartNote,
    type ChartNoteIssue
} from '../../utils/appointments/noteDoc';
import { INTERNAL_STATUS_HOLD_IN_OFFICE } from '../../utils/constants';
import { useScheduleAppointmentContext } from '../../hooks/useScheduleAppointmentContext';

const inputClass =
    'w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-400 bg-white';

interface FieldProps {
    label: string;
    hint?: string;
    /** Appends the app's standard red-asterisk required marker (see
     *  UploadPatientPhotos.tsx's photo-slot labels) after the label text. */
    required?: boolean;
    children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, hint, required, children }) => (
    <label className="block">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1.5">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </div>
        {children}
        {hint && <div className="text-xs text-gray-400 mt-1.5">{hint}</div>}
    </label>
);

/** "a patient, a date and a reason": builds the quiet under-Save hint. */
function joinWithAnd(items: string[]): string {
    if (items.length <= 1) return items[0] ?? '';
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

const CHART_NOTE_HINTS: Record<ChartNoteIssue, string> = {
    too_long: `Shorten the chart note to ${MAX_NOTE_BODY_LENGTH} characters to save.`,
    text_required: 'Add some text to the chart note to save.'
};

function missingFieldsHint(draft: AppointmentDraft): string | null {
    const missing: string[] = [];
    if (!draft.patientId) missing.push('a patient');
    if (!draft.date) missing.push('a date');
    if (!draft.reason.trim()) missing.push('a reason');
    if (missing.length === 0) return null;
    return `Add ${joinWithAnd(missing)} to save.`;
}

interface ComposerProps {
    draft: AppointmentDraft;
    editingAppointment: Appointment | null;
    appointments: Appointment[];
    patients: DashboardPatient[];
    isSaving: boolean;
    todayIso: string;
    onChange: (draft: AppointmentDraft) => void;
    onSave: () => void;
    onClose: () => void;
    /** See ChartNotesField's `autoFocus`. Set when opened via the agenda
     *  row's "Add chart note" nudge. */
    autoFocusChartNote?: boolean;
}

const Composer: React.FC<ComposerProps> = ({
    draft,
    editingAppointment,
    appointments,
    patients,
    isSaving,
    todayIso,
    onChange,
    onSave,
    onClose,
    autoFocusChartNote
}) => {
    const set = <K extends keyof AppointmentDraft>(key: K, value: AppointmentDraft[K]) =>
        onChange({ ...draft, [key]: value });

    // NEW appointment: fetch live "why ODO asked for this" context, gated
    // on internal status (not the action label) - see
    // INTERNAL_STATUS_HOLD_IN_OFFICE in utils/constants.ts. A patient not
    // found in the loaded list, or with a missing/unrecognized status,
    // defaults to "not on Hold-2" - the same conservative default the
    // server uses.
    const isNewAppointment = !draft.id;
    const selectedPatient = draft.patientId
        ? (patients.find((p) => p.id === draft.patientId) ?? null)
        : null;
    const isPatientOnInOfficeHold =
        selectedPatient?.internalCaseStatus === INTERNAL_STATUS_HOLD_IN_OFFICE;
    // EDIT appointment: read the snapshot the backend captured on the
    // appointment at creation time instead - no live re-fetch, so the card
    // matches what was true when it was booked (unaffected by the gate
    // above, which only governs the live NEW-mode fetch).
    const scheduleContext = useScheduleAppointmentContext(
        isNewAppointment && isPatientOnInOfficeHold ? draft.patientId : null
    );
    const odoOriginEvent = isNewAppointment
        ? (scheduleContext.data ?? null)
        : (editingAppointment?.originEvent ?? null);
    const odoContextLoading =
        isNewAppointment && isPatientOnInOfficeHold && scheduleContext.isLoading;

    const sameDayCount = appointments.filter(
        (a) => a.date === draft.date && a.canceledAt === null && a.id !== draft.id
    ).length;

    // Same count-dot data the left-rail calendar shows, reused for the Date
    // field's popover calendar.
    const dateCounts = useMemo(() => countAppointmentsByDate(appointments), [appointments]);

    // What ChartNotesField prefills the title with the first time the editor
    // opens for a brand-new note (no linked note to seed a real title from).
    const derivedNoteTitle = buildAppointmentNoteTitle(draft.reason, formatShortDate(draft.date));

    const chartNote = checkChartNote(
        tiptapDocToPlainText(draft.chartNote),
        !isDocEmpty(draft.chartNote)
    );
    const canSave =
        !!draft.patientId && !!draft.date && !!draft.reason.trim() && !chartNote.issue && !isSaving;
    const chartNoteHint = chartNote.issue ? CHART_NOTE_HINTS[chartNote.issue] : null;
    const missingHint = isSaving ? null : (missingFieldsHint(draft) ?? chartNoteHint);

    return (
        <>
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
                <Field label="Patient">
                    <PatientPicker
                        patients={patients}
                        selectedPatientId={draft.patientId}
                        onSelect={(patientId) => set('patientId', patientId)}
                    />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Date">
                        <DateField
                            value={draft.date}
                            onChange={(date) => set('date', date)}
                            counts={dateCounts}
                            todayIso={todayIso}
                        />
                    </Field>
                    <Field label="Time" hint="Optional">
                        <TimeField value={draft.time} onChange={(time) => set('time', time)} />
                    </Field>
                </div>
                {sameDayCount > 0 && (
                    <p className="-mt-2 text-xs text-gray-400">
                        {sameDayCount} other appointment{sameDayCount > 1 ? 's' : ''} already on{' '}
                        {formatShortDate(draft.date)}.
                    </p>
                )}

                <Field label="Reason" required>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {APPOINTMENT_REASONS.map((reason) => (
                            <button
                                type="button"
                                key={reason}
                                onClick={() => set('reason', reason)}
                                className={`whitespace-nowrap rounded-lg px-2.5 py-1.5 text-[12.5px] border transition-colors ${
                                    draft.reason === reason
                                        ? 'bg-gray-900 text-white border-gray-900'
                                        : 'border-gray-200 text-gray-600 hover:border-gray-400'
                                }`}
                            >
                                {reason}
                            </button>
                        ))}
                    </div>
                    <input
                        value={draft.reason}
                        onChange={(e) => set('reason', e.target.value)}
                        maxLength={MAX_APPOINTMENT_REASON_LENGTH}
                        placeholder="Or type a reason"
                        className={inputClass}
                    />
                </Field>

                {draft.patientId && (
                    <OdoContextCard
                        patientId={draft.patientId}
                        originEvent={odoOriginEvent}
                        isLoading={odoContextLoading}
                    />
                )}

                <ChartNotesField
                    patientId={draft.patientId}
                    appointmentId={draft.id ?? null}
                    linkedNoteId={editingAppointment?.noteId ?? null}
                    isEditingAppointment={!!draft.id}
                    value={draft.chartNote}
                    onChange={(value) => set('chartNote', value)}
                    title={draft.chartNoteTitle}
                    onTitleChange={(title) => set('chartNoteTitle', title)}
                    onSeed={(doc, title) =>
                        onChange({ ...draft, chartNote: doc, chartNoteTitle: title })
                    }
                    derivedTitle={derivedNoteTitle}
                    bodyLength={chartNote.length}
                    autoFocus={autoFocusChartNote}
                />
            </div>
            <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-gray-200 shrink-0">
                <span className="text-xs text-gray-400">{missingHint}</span>
                <div className="flex gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-gray-200 px-4 py-2 text-[13px] font-medium text-gray-600 hover:bg-gray-50"
                    >
                        Discard
                    </button>
                    <button
                        type="button"
                        disabled={!canSave}
                        onClick={onSave}
                        className="rounded-lg bg-gray-900 px-4 py-2 text-[13px] font-medium text-white hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        {draft.id ? 'Save changes' : 'Add appointment'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default Composer;
